import React, { useState, useEffect } from "react";
import { RotateCcw } from "lucide-react";
import { DEFAULT_SLOTS, SLOT_KEYS, SLOT_LABELS } from "@/lib/slots";

/**
 * Compact editor for the 3 time slots (AM / PM / SOIRÉE).
 * Saves the full slots object via onSave whenever a value commits (blur).
 * Optional "reset" hook is exposed by the parent if needed.
 */
export default function SlotsEditor({ slots, onSave, testidPrefix = "slot", onReset, showReset = true }) {
  const [local, setLocal] = useState(slots || DEFAULT_SLOTS);

  useEffect(() => {
    setLocal(slots || DEFAULT_SLOTS);
  }, [slots]);

  const setField = (key, side, value) => {
    setLocal({ ...local, [key]: { ...local[key], [side]: value } });
  };

  const commit = (next) => {
    onSave && onSave(next);
  };

  const handleBlur = () => {
    // Basic sanity: ensure all values are HH:MM strings
    const ok = SLOT_KEYS.every(
      (k) => /^\d{2}:\d{2}$/.test(local[k]?.start || "") && /^\d{2}:\d{2}$/.test(local[k]?.end || "")
    );
    if (ok) commit(local);
  };

  const resetDefaults = () => {
    setLocal(DEFAULT_SLOTS);
    if (onReset) onReset();
    else commit(DEFAULT_SLOTS);
  };

  return (
    <div data-testid={`${testidPrefix}-editor`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {SLOT_KEYS.map((k) => (
          <div key={k} className="border border-black p-3" data-testid={`${testidPrefix}-${k}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mb-2">
              {SLOT_LABELS[k]}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block font-mono text-[9px] uppercase tracking-widest text-[var(--muted-2)] mb-1">
                  Début
                </label>
                <input
                  type="time"
                  value={local[k]?.start || ""}
                  onChange={(e) => setField(k, "start", e.target.value)}
                  onBlur={handleBlur}
                  className="input-box !py-1"
                  data-testid={`${testidPrefix}-${k}-start`}
                />
              </div>
              <div>
                <label className="block font-mono text-[9px] uppercase tracking-widest text-[var(--muted-2)] mb-1">
                  Fin
                </label>
                <input
                  type="time"
                  value={local[k]?.end || ""}
                  onChange={(e) => setField(k, "end", e.target.value)}
                  onBlur={handleBlur}
                  className="input-box !py-1"
                  data-testid={`${testidPrefix}-${k}-end`}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
          Les changements sont sauvegardés automatiquement.
        </p>
        {showReset && (
          <button
            type="button"
            onClick={resetDefaults}
            className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black inline-flex items-center gap-1"
            data-testid={`${testidPrefix}-reset`}
          >
            <RotateCcw size={11} strokeWidth={2.5} /> Réinitialiser
          </button>
        )}
      </div>
    </div>
  );
}
